import { AppointmentPreferences } from "@physiofit-digital/physiofit_types";
import { t } from "i18next";
import { Fragment } from "react";
import styled from "styled-components";
import { GenericSvg } from "../../assets/svgs/GenericSVG";
import { CustomButton } from "../../core/components/buttons/CustomButton";
import { InfoText } from "../../core/components/texts/InfoText";
import {
  getAppointmentCustomPreferenceIds,
  getAppointmentPreferences,
} from "../../core/store/appointmentSlice";
import {
  getCurrentPracticeAppointmentRequestFlags,
  getCurrentPracticeCustomAppointmentOptions,
} from "../../core/store/chainSlice";
import { useMainStore } from "../../core/store/mainStore";
import { H4, Mini } from "../../core/styles/stringStyles";
import { useStyles } from "../../core/styles/theme";
import { hexWithOpacity } from "../../core/utils/helpers";
import { AppointmentScreenWrapper } from "../onboarding/AppointmentScreenWrapper";
import { enabledCustomOptions } from "./utils/detailOptions";

type Preferences = {
  id: keyof AppointmentPreferences;
  label: string;
  description?: string;
  onClick: () => void;
};

type Row = {
  key: string;
  label: string;
  description?: string;
  isSelected: boolean;
  onClick: () => void;
};

export const AppointmentPreferencesScreen = () => {
  const appointmentPreferences = useMainStore(getAppointmentPreferences);
  const appointmentRequestFlags = useMainStore(
    getCurrentPracticeAppointmentRequestFlags,
  );
  const setAppointmentPreferences = useMainStore(
    (state) => state.setAppointmentPreferences,
  );
  const customOptions = useMainStore(
    getCurrentPracticeCustomAppointmentOptions,
  );
  const customPreferenceIds = useMainStore(getAppointmentCustomPreferenceIds);
  const setCustomPreferenceIds = useMainStore(
    (state) => state.setAppointmentCustomPreferenceIds,
  );
  const theme = useStyles();
  const preferencesList: Preferences[] = [
    ...(appointmentRequestFlags?.hideTherapistPreferences
      ? []
      : ([
          {
            id: "noChangingTherapistDuringTreatment",
            label: "CHANGE_THERAPIST_DURING_TREATMENT_LABEL",
            description: "CHANGE_THERAPIST_DURING_TREATMENT_DESCRIPTION",
            onClick: () =>
              handleTogglePreference("noChangingTherapistDuringTreatment"),
          },
        ] as Preferences[])),
    ...(appointmentRequestFlags?.hideTherapistGenderPreference
      ? []
      : ([
          {
            id: "onlyFemaleTherapists",
            label: "ONLY_FEMALE_THERAPIST_LABEL",
            onClick: () => {
              handleTogglePreference("onlyFemaleTherapists");
            },
          },
          {
            id: "onlyMaleTherapists",
            label: "ONLY_MALE_THERAPIST_LABEL",
            onClick: () => handleTogglePreference("onlyMaleTherapists"),
          },
        ] as Preferences[])),
    ...(appointmentRequestFlags?.hideHouseVisits
      ? []
      : ([
          {
            id: "houseVisits",
            label: "HOUSE_VISIT_LABEL",
            description: "HOUSE_VISIT_DESCRIPTION",
            onClick: () => handleTogglePreference("houseVisits"),
          },
        ] as Preferences[])),
    ...(appointmentRequestFlags?.hideExtendedTime
      ? []
      : ([
          {
            id: "extendedTime",
            label: "EXTENDED_TIME_LABEL",
            onClick: () => handleTogglePreference("extendedTime"),
          },
        ] as Preferences[])),
  ];

  const handleTogglePreference = (
    preferenceId: keyof AppointmentPreferences,
  ) => {
    const currentValue = appointmentPreferences?.[preferenceId] || false;
    setAppointmentPreferences({
      ...appointmentPreferences,
      [preferenceId]: !currentValue,
      ...(preferenceId === "onlyMaleTherapists"
        ? { onlyFemaleTherapists: false }
        : {}),
      ...(preferenceId === "onlyFemaleTherapists" && {
        onlyMaleTherapists: false,
      }),
    });
  };

  const handleToggleCustomPreference = (optionId: string) =>
    setCustomPreferenceIds(
      customPreferenceIds.includes(optionId)
        ? customPreferenceIds.filter((id) => id !== optionId)
        : [...customPreferenceIds, optionId],
    );

  // Practice-typed text is shown as typed: i18next would read ":" and "." in it as key separators.
  const rows: Row[] = [
    ...preferencesList.map((preference) => ({
      key: preference.id,
      label: t(preference.label),
      description: preference.description && t(preference.description),
      isSelected:
        preference.id === "noChangingTherapistDuringTreatment"
          ? !appointmentPreferences?.[preference.id]
          : !!appointmentPreferences?.[preference.id],
      onClick: preference.onClick,
    })),
    ...enabledCustomOptions(customOptions).map((option) => ({
      key: option.optionId,
      label: option.name,
      description: option.description,
      isSelected: customPreferenceIds.includes(option.optionId),
      onClick: () => handleToggleCustomPreference(option.optionId),
    })),
  ];

  return (
    <AppointmentScreenWrapper>
      <InfoText
        headline={t("APPOINTMENT_PREFERENCES_SCREEN_TITLE")}
        text={[t("APPOINTMENT_PREFERENCES_SCREEN_DESCRIPTION")]}
      />
      <Container>
        {rows.map((row, index) => (
          <Fragment key={row.key}>
            <Button onClick={row.onClick}>
              <GenericSvg
                name={
                  row.isSelected
                    ? "CheckBoxSquareActive"
                    : "CheckBoxSquareInactive"
                }
                width={24}
                height={24}
                primaryColor={
                  row.isSelected
                    ? theme.colors.white
                    : hexWithOpacity(theme.colors.dark, 0.5)
                }
                secondaryColor={row.isSelected ? theme.colors.dark : undefined}
              />
              <ButtonTextContainer>
                <ButtonLabel>{row.label}</ButtonLabel>
                {row.description && (
                  <ButtonDescription>{row.description}</ButtonDescription>
                )}
              </ButtonTextContainer>
            </Button>
            {index < rows.length - 1 && <Divider />}
          </Fragment>
        ))}
      </Container>
    </AppointmentScreenWrapper>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Button = styled(CustomButton)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
`;

const ButtonTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 80%;
`;
const ButtonLabel = styled(H4)`
  color: ${(props) => props.theme.colors.dark};
  text-align: left;
`;
const ButtonDescription = styled(Mini)`
  color: ${(props) => hexWithOpacity(props.theme.colors.dark, 0.5)};
  text-align: left;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) =>
    hexWithOpacity(props.theme.colors.medium, 0.1)};
  margin: 10px 0;
`;
